import { useEffect, useRef } from 'react';
import type { ConfirmRequest } from '@/hooks/useConfirm';

interface ConfirmDialogProps {
  request: (ConfirmRequest & { resolve?: unknown }) | null;
  onAnswer: (value: boolean) => void;
}

/**
 * Modal confirmation for destructive or hard-to-reverse actions only
 * (End Survey, Discard Draft). Vehicle taps never go through here.
 */
export function ConfirmDialog({ request, onAnswer }: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!request) return;
    confirmRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onAnswer(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [request, onAnswer]);

  if (!request) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onAnswer(false);
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={request.body ? 'confirm-body' : undefined}
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl pb-safe"
      >
        <h2 id="confirm-title" className="text-lg font-bold text-slate-900">
          {request.title}
        </h2>
        {request.body ? (
          <p id="confirm-body" className="mt-2 whitespace-pre-line text-slate-600">
            {request.body}
          </p>
        ) : null}
        <div className="mt-5 flex flex-col gap-2">
          <button
            ref={confirmRef}
            type="button"
            className={request.destructive ? 'btn-danger w-full' : 'btn-primary w-full'}
            onClick={() => onAnswer(true)}
          >
            {request.confirmLabel}
          </button>
          <button type="button" className="btn-secondary w-full" onClick={() => onAnswer(false)}>
            {request.cancelLabel ?? 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}
